/**
 * ProjectPickerButton — belge formlarında (fatura, teklif, sipariş, irsaliye) projeye
 * bağlama işini tek bir düğme + modal içinde toplar.
 *
 * Neden: proje alanı formda her zaman kullanılmıyor ama sürekli yer kaplıyordu.
 * Bu düğme yalnızca bir satır yer kaplar; bağ varsa proje adını üzerinde gösterir.
 *
 * Modal içinde önce projenin ne olduğu tek satırla anlatılır (detaylı anlatım için
 * "projects.guide" rehberi açılır), ardından sistemdeki projeler listelenir.
 *
 * Öneri listesinde yalnız aktif projeler görünür; düzenlenen belge tamamlanmış veya
 * iptal edilmiş bir projeye bağlıysa o proje de durum etiketiyle gösterilir, böylece
 * mevcut bağ kazara kaybolmaz.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Radio,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { getProjectsPaged, getProjectById, type Project } from '../../../services/projectService';
import GuideButton from '../../common/guide/GuideButton';

interface Props {
  /** Seçili proje kimliği; bağ yoksa null. */
  value: number | null;
  onChange: (projectId: number | null) => void;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  /** Düğme üzerindeki proje adının en fazla kaç karakter gösterileceği. */
  maxLabelChars?: number;
}

const PAGE_SIZE = 50;

const ProjectPickerButton: React.FC<Props> = ({
  value,
  onChange,
  disabled = false,
  size = 'medium',
  maxLabelChars = 18,
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (tr: string, en: string) => (isTr ? tr : en);

  const [open, setOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Modal içindeki geçici seçim; "Seç" denmeden forma yazılmaz. */
  const [draft, setDraft] = useState<number | null>(value);
  /** Düğme etiketinde ad gösterebilmek için bağlı projenin kendisi. */
  const [linkedProject, setLinkedProject] = useState<Project | null>(null);

  // Bağlı projenin adı: liste açılmadan da düğmede görünmesi gerekir.
  useEffect(() => {
    if (!value) {
      setLinkedProject(null);
      return;
    }
    if (linkedProject?.id === value) return;

    let cancelled = false;
    getProjectById(value)
      .then((project) => {
        if (!cancelled) setLinkedProject(project);
      })
      .catch(() => {
        /* proje silinmiş olabilir — düğme genel etiketle görünür, bağ korunur */
      });
    return () => {
      cancelled = true;
    };
  }, [value, linkedProject]);

  const loadProjects = useCallback(async (term: string) => {
    try {
      setLoading(true);
      setError(null);
      const result = await getProjectsPaged({
        page: 1,
        pageSize: PAGE_SIZE,
        search: term || undefined,
        status: 'active',
      });
      setProjects(result.data);
      setTotalCount(result.totalCount);
    } catch (e) {
      setProjects([]);
      setTotalCount(0);
      setError(e instanceof Error ? e.message : tt('Projeler alınamadı.', 'Projects could not be loaded.'));
    } finally {
      setLoading(false);
    }
    // tt yalnızca dil değişince değişir; bağımlılığa eklemek gereksiz yükleme yapar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Modal açıkken arama yazıldıkça listeyi yenile (yazma sırasında beklet)
  useEffect(() => {
    if (!open) return;
    const handle = setTimeout(() => loadProjects(search), search ? 300 : 0);
    return () => clearTimeout(handle);
  }, [open, search, loadProjects]);

  const handleOpen = () => {
    setDraft(value);
    setSearch('');
    setOpen(true);
  };

  const handleConfirm = () => {
    onChange(draft);
    // Seçilen proje listedeyse adını hemen düğmeye yansıt (yeniden istek atmadan)
    const chosen = draft ? projects.find((p) => p.id === draft) ?? null : null;
    if (chosen) setLinkedProject(chosen);
    if (!draft) setLinkedProject(null);
    setOpen(false);
  };

  const statusLabel = (status: string) => {
    if (status === 'completed') return tt('Tamamlandı', 'Completed');
    if (status === 'cancelled') return tt('İptal edildi', 'Cancelled');
    return null;
  };

  /** Bağlı proje aktif listede yoksa (tamamlanmış/iptal) listeye başa eklenir. */
  const listedProjects =
    linkedProject && !projects.some((p) => p.id === linkedProject.id)
      ? [linkedProject, ...projects]
      : projects;

  const projectTitle = (project: Project) =>
    project.code ? `${project.name} (${project.code})` : project.name;

  const buttonLabel = (() => {
    if (!linkedProject) return tt('Proje', 'Project');
    const title = projectTitle(linkedProject);
    return title.length > maxLabelChars ? `${title.slice(0, maxLabelChars)}…` : title;
  })();

  const isLinked = value !== null;
  /** Arama boşken hiç sonuç yoksa sistemde gerçekten proje yok demektir. */
  const systemHasNoProject = !loading && search.trim() === '' && listedProjects.length === 0;

  return (
    <>
      <Tooltip
        title={
          isLinked && linkedProject
            ? tt(`Bağlı proje: ${projectTitle(linkedProject)}`, `Linked project: ${projectTitle(linkedProject)}`)
            : tt('Belgeyi bir projeye bağla (opsiyonel)', 'Link this document to a project (optional)')
        }
      >
        <Button
          variant="outlined"
          color={isLinked ? 'success' : 'inherit'}
          size={size}
          startIcon={<AccountTreeIcon />}
          onClick={handleOpen}
          disabled={disabled}
        >
          {buttonLabel}
        </Button>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <AccountTreeIcon color="primary" />
              <Typography variant="h6">{tt('Proje Seç', 'Select Project')}</Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <GuideButton guideKey="projects.guide" open={guideOpen} onOpenChange={setGuideOpen} />
              <IconButton onClick={() => setOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Alert severity="info" icon={<HelpOutlineIcon />} sx={{ mb: 2, alignItems: 'center' }}>
            <Typography variant="body2" component="span">
              <b>{tt('Proje nedir?', 'What is a project?')}</b>{' '}
              {tt(
                'Belgeyi bir işe/projeye bağlarsınız; tutarlar değişmez.',
                'You link the document to a job/project; amounts do not change.'
              )}
            </Typography>
            <Button size="small" onClick={() => setGuideOpen(true)} sx={{ ml: 1, py: 0, minWidth: 0, textTransform: 'none' }}>
              {tt('Detaylı anlatım', 'Detailed guide')}
            </Button>
          </Alert>

          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tt('Proje adı veya kodu ara...', 'Search project name or code...')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: loading ? <CircularProgress size={16} /> : undefined,
            }}
            sx={{ mb: 1 }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          )}

          <Divider />

          <List dense sx={{ maxHeight: 320, overflowY: 'auto' }}>
            {/* Projeye bağlamama seçeneği her zaman en üstte */}
            <ListItemButton selected={draft === null} onClick={() => setDraft(null)}>
              <Radio checked={draft === null} size="small" tabIndex={-1} disableRipple />
              <ListItemText
                primary={tt('Projeye bağlama', 'No project')}
                secondary={tt('Belge hiçbir projeye dahil edilmez', 'The document is not included in any project')}
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItemButton>

            {listedProjects.map((project) => {
              const badge = statusLabel(project.status);
              return (
                <ListItemButton
                  key={project.id}
                  selected={draft === project.id}
                  onClick={() => setDraft(project.id)}
                >
                  <Radio checked={draft === project.id} size="small" tabIndex={-1} disableRipple />
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{projectTitle(project)}</span>
                        {badge && <Chip size="small" variant="outlined" color="warning" label={badge} />}
                      </Box>
                    }
                    secondary={project.description || undefined}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                  />
                </ListItemButton>
              );
            })}

            {systemHasNoProject && (
              <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {tt('Sistemde henüz proje yok.', 'There are no projects in the system yet.')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {tt(
                    'Proje eklemek için Ön Muhasebe → Projeler sayfasını kullanabilirsiniz.',
                    'You can add projects from the Accounting → Projects page.'
                  )}
                </Typography>
              </Box>
            )}

            {!loading && !systemHasNoProject && listedProjects.length === 0 && (
              <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {tt('Aramanıza uygun proje bulunamadı.', 'No project matches your search.')}
                </Typography>
              </Box>
            )}
          </List>

          {totalCount > listedProjects.length && (
            <Typography variant="caption" color="text.secondary">
              {tt(
                `${totalCount} projeden ilk ${listedProjects.length} tanesi gösteriliyor. Aramayı daraltın.`,
                `Showing the first ${listedProjects.length} of ${totalCount} projects. Narrow your search.`
              )}
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>{tt('İptal', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleConfirm}>
            {tt('Seç', 'Select')}
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default ProjectPickerButton;
